import React, { useCallback, useMemo, useState } from 'react';
import {
  Button, Typography, Box, Stack, alpha, useTheme, CircularProgress, ToggleButton,
  ToggleButtonGroup, LinearProgress,
} from '@mui/material';
import {
  UploadFile, Replay, TaskAlt, ErrorOutline, EditOutlined,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import type { AdmissionCycle } from '../../types/admissions';
import { admissionApplicationService } from '../../services/admissions/admissionApplicationService';
import {
  buildApplicationLookups,
  buildBulkApplicationsXlsx,
  parseBulkApplicationsXlsx,
  toBulkApplicationsPayload,
  withFieldError,
  type ParsedBulkApplicationRow,
} from '../../utils/bulkApplicationsXlsx';
import { downloadXlsxBlob } from '../../utils/xlsx';
import BulkApplicationsReviewTable from './BulkApplicationsReviewTable';
import ConfirmationDialog from '../common/ConfirmationDialog';
import StepDialog from '../common/StepDialog';
import BulkSheetPickStage from './BulkSheetPickStage';
import { grLabel } from '../../utils/personRef';
import useTimezone from '../../hooks/useTimezone';

interface Props {
  open: boolean;
  cycle: AdmissionCycle;
  onClose: () => void;
  onCreated: () => void;
}

type Stage = 'pick' | 'review' | 'result';

const BULK_UPLOAD_CAP = 500;

/**
 * Three-stage guided flow — prepare (template + upload), review (every row
 * validated with the admission form's own rules, inline fix / remove),
 * done (result). The template is built per cycle from its admission form;
 * the backend processes in chunks of 50.
 */
const BulkApplicationsDialog: React.FC<Props> = ({ open, cycle, onClose, onCreated }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { getToday } = useTimezone();

  const [stage, setStage] = useState<Stage>('pick');
  const [buildingTemplate, setBuildingTemplate] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<ParsedBulkApplicationRow[]>([]);
  const [filter, setFilter] = useState<'all' | 'ok' | 'error'>('all');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{
    created: number;
    failed: number;
    total: number;
    /** Created but recorded as Ineligible, with why. */
    ineligible: Array<{ name: string; reasons: string[] }>;
  } | null>(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [precheckRunning, setPrecheckRunning] = useState(false);

  const lookups = useMemo(() => buildApplicationLookups(cycle), [cycle]);

  const resetFlow = useCallback(() => {
    setStage('pick');
    setRows([]);
    setFilter('all');
    setResult(null);
    setUploadedFileName(null);
  }, []);

  const closeImmediately = () => {
    resetFlow();
    onClose();
  };

  const handleClose = () => {
    if (submitting) return;
    if (stage === 'review' && rows.length > 0) {
      setDiscardConfirmOpen(true);
      return;
    }
    closeImmediately();
  };

  const onDownloadTemplate = async () => {
    try {
      setBuildingTemplate(true);
      const blob = await buildBulkApplicationsXlsx(cycle);
      downloadXlsxBlob(`admissions-${cycle.name.replace(/\s+/g, '-')}-${getToday()}.xlsx`, blob);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to build template', { variant: 'error' });
    } finally {
      setBuildingTemplate(false);
    }
  };

  const runCnicPrecheck = useCallback(
    async (parsedRows: ParsedBulkApplicationRow[]): Promise<ParsedBulkApplicationRow[]> => {
      const cnics = Array.from(
        new Set(
          parsedRows
            .map(r => r.fields.cnic)
            .filter((c): c is string => !!c && c.length === 13),
        ),
      );
      if (cnics.length === 0) return parsedRows;
      setPrecheckRunning(true);
      try {
        const existing = await admissionApplicationService.precheckBulkCnics(cycle._id, cnics);
        if (Object.keys(existing).length === 0) return parsedRows;
        return parsedRows.map(r => {
          if (!r.fields.cnic) return r;
          const hit = existing[r.fields.cnic];
          if (!hit) return r;
          return withFieldError(r, 'cnic', `Already on file (${grLabel(hit)}) — add through New walk-in`);
        });
      } catch (err: any) {
        enqueueSnackbar(err?.message || 'CNIC precheck failed — you can still review and submit', { variant: 'warning' });
        return parsedRows;
      } finally {
        setPrecheckRunning(false);
      }
    },
    [cycle._id, enqueueSnackbar],
  );

  /** Mark ready rows the cycle's eligibility rules would turn away (server
   *  check — the same rules the import applies). Never blocks a row. */
  const withEligibility = useCallback(
    async (current: ParsedBulkApplicationRow[]): Promise<ParsedBulkApplicationRow[]> => {
      const payload = toBulkApplicationsPayload(current, cycle, lookups);
      if (payload.length === 0) return current;
      try {
        const reasons = await admissionApplicationService.previewBulkEligibility(cycle._id, payload);
        return current.map(r => ({ ...r, ineligibleReasons: r.clientStatus === 'ok' ? reasons[r.rowNumber] : undefined }));
      } catch (err: any) {
        enqueueSnackbar(err?.message || 'Could not check eligibility — rows are still checked when created', { variant: 'warning' });
        return current;
      }
    },
    [cycle, lookups, enqueueSnackbar],
  );

  // Edits in the review table re-check eligibility (a corrected date of
  // birth may clear the warning).
  const onRowsChange = useCallback(async (next: ParsedBulkApplicationRow[]) => {
    setRows(next);
    setRows(await withEligibility(next));
  }, [withEligibility]);

  // In-file dedupe: catch duplicate CNICs WITHIN the uploaded sheet before
  // sending anything to the backend.
  const flagInFileDuplicates = (parsedRows: ParsedBulkApplicationRow[]): ParsedBulkApplicationRow[] => {
    const seenCnics = new Map<string, number>();
    return parsedRows.map(r => {
      const cnic = r.fields.cnic;
      if (!cnic || cnic.length !== 13) return r;
      const prev = seenCnics.get(cnic);
      if (prev !== undefined) {
        return withFieldError(r, 'cnic', `Same CNIC as row ${prev}`);
      }
      seenCnics.set(cnic, r.rowNumber);
      return r;
    });
  };

  const handleFile = useCallback(async (file: File) => {
    setParsing(true);
    try {
      const parsed = await parseBulkApplicationsXlsx(file, cycle, lookups);
      if (parsed.fileError) {
        enqueueSnackbar(parsed.fileError, { variant: 'error' });
        return;
      }
      if (parsed.rows.length === 0) {
        enqueueSnackbar(
          parsed.skippedSampleRow
            ? 'This file only has the template’s sample row. Add your applicants under the headings (you can type over the sample row), then upload again.'
            : 'No applicants found in the file. Add one applicant per row under the headings, then upload again.',
          { variant: 'warning' },
        );
        return;
      }
      if (parsed.rows.length > BULK_UPLOAD_CAP) {
        enqueueSnackbar(
          `This file has ${parsed.rows.length} rows — the limit is ${BULK_UPLOAD_CAP} per upload. Split it and try again.`,
          { variant: 'error' },
        );
        return;
      }
      // 1. In-file dedupe pass
      let next = flagInFileDuplicates(parsed.rows);
      // 2. Backend CNIC existence check
      next = await runCnicPrecheck(next);
      // 3. Eligibility preview (warning only)
      next = await withEligibility(next);
      setRows(next);
      setUploadedFileName(file.name);
      setFilter('all');
      setStage('review');
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to read the file', { variant: 'error' });
    } finally {
      setParsing(false);
    }
  }, [cycle, lookups, enqueueSnackbar, runCnicPrecheck, withEligibility]);

  const okCount = rows.filter(r => r.clientStatus === 'ok').length;
  const errorCount = rows.length - okCount;
  const ineligibleCount = rows.filter(r => r.ineligibleReasons?.length).length;

  const onSubmit = async () => {
    const payload = toBulkApplicationsPayload(rows, cycle, lookups);
    if (payload.length === 0) {
      enqueueSnackbar('No ready-to-upload rows. Fix the errors or remove them.', { variant: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await admissionApplicationService.bulkCreateApplications(cycle._id, payload);
      const nameOfRow = new Map(rows.map(r => [r.rowNumber, String(r.fields.name || `Row ${r.rowNumber}`)]));
      setResult({
        created: res.summary.created,
        failed: res.summary.failed,
        total: res.summary.total,
        ineligible: res.created
          .filter(c => c.ineligibleReasons?.length)
          .map(c => ({ name: nameOfRow.get(c.rowNumber) ?? `Row ${c.rowNumber}`, reasons: c.ineligibleReasons! })),
      });
      // Any server-side failures: flag the matching rows so operator can fix + re-upload.
      if (res.failed.length > 0) {
        const errorByRow = new Map(res.failed.map(e => [e.rowNumber, e.error]));
        setRows(prev => prev.map(r =>
          errorByRow.has(r.rowNumber)
            ? { ...r, clientStatus: 'error' as const, clientError: errorByRow.get(r.rowNumber) }
            : r,
        ));
      }
      setStage('result');
      onCreated();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Bulk upload failed', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  const stepIndex = stage === 'pick' ? 0 : stage === 'review' ? 1 : 2;
  const busy = parsing || precheckRunning;

  const renderPickStage = () => (
    <BulkSheetPickStage
      templateText={<>A spreadsheet whose columns match this cycle&apos;s admission form. Gender, class and other
        choices have drop-down lists; required columns are marked *.</>}
      templateButtonLabel="Download template (.xlsx)"
      buildingTemplate={buildingTemplate}
      onDownloadTemplate={onDownloadTemplate}
      onFile={handleFile}
      busy={busy}
      busyText={precheckRunning ? 'Checking CNICs against school records…' : 'Reading your file…'}
      capText={`.xlsx · up to ${BULK_UPLOAD_CAP} applicants`}
      tips={[
        'Nothing is created until you review every row and confirm.',
        'Each row is checked with the same rules as the admission form — missing answers, CNICs, phone numbers, dates and choices.',
        'Applicants already in the school\'s records are flagged; add those through New walk-in so their record is kept.',
        'Families are linked by staff afterwards with Resolve family on each applicant — a brother or sister already at the school is matched there by the father\'s CNIC.',
        'The template\'s sample row is ignored, so it\'s fine if you forget to delete it.',
      ]}
    />
  );

  const renderReviewStage = () => (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ md: 'center' }}
        sx={{ p: 1.5, pl: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <UploadFile fontSize="small" sx={{ color: 'text.secondary' }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>{uploadedFileName ?? 'Uploaded file'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {rows.length} applicant{rows.length === 1 ? '' : 's'} ·{' '}
              <Box component="span" sx={{ color: 'success.main', fontWeight: 600 }}>{okCount} ready</Box>
              {errorCount > 0 && (
                <> · <Box component="span" sx={{ color: 'error.main', fontWeight: 600 }}>{errorCount} need{errorCount === 1 ? 's' : ''} fixing</Box></>
              )}
              {ineligibleCount > 0 && (
                <> <Box component="span" sx={{ color: 'warning.dark', fontWeight: 600 }}>({ineligibleCount} of them not eligible)</Box></>
              )}
            </Typography>
          </Box>
        </Stack>
        <ToggleButtonGroup
          value={filter}
          exclusive
          size="small"
          onChange={(_, v) => v && setFilter(v)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, fontWeight: 500 } }}
        >
          <ToggleButton value="all">All {rows.length}</ToggleButton>
          <ToggleButton value="ok">Ready {okCount}</ToggleButton>
          <ToggleButton value="error" disabled={errorCount === 0}>Needs fixing {errorCount}</ToggleButton>
        </ToggleButtonGroup>
        <Button size="small" startIcon={<Replay />} onClick={resetFlow} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Upload another file
        </Button>
      </Stack>

      {errorCount > 0 && (
        <Typography variant="body2" color="text.secondary">
          Fix a row with <EditOutlinedIcon /> or remove it. Only ready rows are submitted — you can also submit the
          ready ones now and upload the rest later.
        </Typography>
      )}

      {ineligibleCount > 0 && (
        <Typography variant="body2" color="warning.dark">
          {ineligibleCount === 1 ? '1 applicant doesn’t' : `${ineligibleCount} applicants don’t`} meet the cycle&apos;s eligibility rules and will be
          recorded as Ineligible. Check the date of birth or details if that looks wrong.
        </Typography>
      )}

      {submitting && <LinearProgress sx={{ borderRadius: 1 }} />}

      <BulkApplicationsReviewTable rows={rows} cycle={cycle} lookups={lookups} filter={filter} onChange={onRowsChange} />
    </Stack>
  );

  const renderResultStage = () => {
    const allGood = result?.failed === 0;
    return (
      <Box sx={{ textAlign: 'center', py: { xs: 2, sm: 4 }, maxWidth: 440, mx: 'auto' }}>
        <Box sx={{
          width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(allGood ? theme.palette.success.main : theme.palette.warning.main, 0.12),
          color: allGood ? 'success.main' : 'warning.main',
        }}>
          {allGood ? <TaskAlt sx={{ fontSize: 34 }} /> : <ErrorOutline sx={{ fontSize: 34 }} />}
        </Box>
        <Typography variant="h6" fontWeight={700}>
          {result?.created} application{result?.created === 1 ? '' : 's'} created
        </Typography>
        {!!result?.ineligible.length && (
          <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, textAlign: 'left', bgcolor: alpha(theme.palette.warning.main, 0.08) }}>
            <Typography variant="body2" fontWeight={600}>
              {result.ineligible.length} recorded as Ineligible
            </Typography>
            {result.ineligible.map(i => (
              <Typography key={i.name} variant="body2" color="text.secondary">{i.name} — {i.reasons.join('; ')}</Typography>
            ))}
          </Box>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {allGood
            ? 'The applications are now in the Applications tab.'
            : `${result?.failed} row${result?.failed === 1 ? ' was' : 's were'} rejected by the server. Go back to the review to see why, fix them and submit again.`}
        </Typography>
        {!allGood && (
          <Button
            variant="outlined"
            sx={{ mt: 2.5, textTransform: 'none' }}
            onClick={() => { setRows(prev => prev.filter(r => r.clientStatus === 'error')); setFilter('error'); setStage('review'); }}
          >
            Review rejected rows
          </Button>
        )}
      </Box>
    );
  };

  const footer = (
    <>
      <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: 'none', color: 'text.secondary' }}>
        {stage === 'result' ? 'Close' : 'Cancel'}
      </Button>
      <Box sx={{ flex: 1 }} />
      {stage === 'review' && (
        <Button
          variant="contained"
          disableElevation
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={onSubmit}
          disabled={submitting || okCount === 0}
          sx={{ textTransform: 'none' }}
        >
          {submitting ? 'Creating applications…' : `Create ${okCount} application${okCount === 1 ? '' : 's'}`}
        </Button>
      )}
      {stage === 'result' && (
        <Button variant="contained" disableElevation onClick={handleClose} sx={{ textTransform: 'none' }}>Done</Button>
      )}
    </>
  );

  return (
    <>
      <StepDialog
        open={open}
        onClose={handleClose}
        closeDisabled={submitting}
        title="Bulk upload applications"
        subtitle={cycle.name}
        steps={['Prepare', 'Review', 'Done']}
        activeStep={stage === 'result' ? 3 : stepIndex}
        maxWidth={stage === 'review' ? 'xl' : 'md'}
        footer={footer}
      >
        {stage === 'pick' && renderPickStage()}
        {stage === 'review' && renderReviewStage()}
        {stage === 'result' && renderResultStage()}
      </StepDialog>

      <ConfirmationDialog
        open={discardConfirmOpen}
        onClose={() => setDiscardConfirmOpen(false)}
        onConfirm={() => { setDiscardConfirmOpen(false); closeImmediately(); }}
        title="Discard this upload?"
        message="Your edits to the review table will be lost. The uploaded file is not saved anywhere."
        confirmText="Discard"
        cancelText="Keep editing"
        confirmColor="error"
      />
    </>
  );
};

const EditOutlinedIcon: React.FC = () => (
  <EditOutlined sx={{ fontSize: 16, verticalAlign: 'text-bottom', mx: 0.25 }} />
);

export default BulkApplicationsDialog;
